import React from 'react';
import { DateUtil } from "@/utils/dateUtil.ts";
import { PrintUtil } from "@/utils/printUtil.ts";
import type { HeaderConfig } from '@/components/Print/UnifiedPrintModal';

interface PrescriptionContentProps {
  data: any;
  translations: any;
  language: string;
  clinicInfo: any;
  doctorInfo: any;
  patientInfo: any;
  settings: any;
  currentUser: any;
  headerConfig?: HeaderConfig;
}

export function PrescriptionContent({
                                      data,
                                      translations: t,
                                      language,
                                      clinicInfo,
                                      doctorInfo,
                                      patientInfo,
                                      settings,
                                      currentUser,
                                      headerConfig,
                                    }: Readonly<PrescriptionContentProps>) {

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const normalizeText = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  };

  const hasText = (value: unknown): boolean => normalizeText(value).length > 0;

  const isZeroLike = (value: string): boolean => {
    const v = normalizeText(value)
        .toLowerCase()
        .replace(/ml|puffs?|amp|tabs?/gi, '')
        .replace('½', '0.5')
        .replace('¼', '0.25')
        .trim();
    return v === '' || v === '0' || v === '0.0' || v === '0.00';
  };

  // ── Translation map ───────────────────────────────────────────────────────────

  const phraseMap: Record<string, string> = {
    'after breakfast':  t.afterBreakfast  ?? 'After Breakfast',
    'before breakfast': t.beforeBreakfast ?? 'Before Breakfast',
    'with breakfast':   t.withBreakfast   ?? 'With Breakfast',
    'after lunch':      t.afterLunch      ?? 'After Lunch',
    'before lunch':     t.beforeLunch     ?? 'Before Lunch',
    'with lunch':       t.withLunch       ?? 'With Lunch',
    'after dinner':     t.afterDinner     ?? 'After Dinner',
    'before dinner':    t.beforeDinner    ?? 'Before Dinner',
    'with dinner':      t.withDinner      ?? 'With Dinner',
    'after food':       t.afterFood       ?? 'After Food',
    'before food':      t.beforeFood      ?? 'Before Food',
    'with food':        t.withFood        ?? 'With Food',
    'empty stomach':    t.emptyStomach    ?? 'Empty Stomach',
    'at bedtime':       t.bedtime         ?? 'At Bedtime',
    'bedtime':          t.bedtime         ?? 'At Bedtime',
    'morning':          t.morning         ?? 'Morning',
    'afternoon':        t.afternoon       ?? 'Afternoon',
    'evening':          t.evening         ?? 'Evening',
    'night':            t.night           ?? 'Night',
    'as directed':      t.asDirected      ?? 'As Directed',
    'sos':              t.sos             ?? 'SOS',
    'days':             t.days            ?? 'days',
    'day':              t.days            ?? 'day',
    'weeks':            t.weeks           ?? 'weeks',
    'week':             t.weeks           ?? 'week',
    'months':           t.months          ?? 'months',
    'month':            t.months          ?? 'month',
  };

  const translatePhrase = (text: string): string => {
    const input = normalizeText(text);
    if (!input) return '';
    let output = input.toLowerCase();
    // Sort longest keys first to prevent 'day' matching inside 'days'
    const keys = Object.keys(phraseMap).sort((a, b) => b.length - a.length);
    for (const key of keys) {
      // Use word boundary \b to prevent 'day' replacing inside 'days'
      output = output.replace(new RegExp(`\\b${key}\\b`, 'gi'), phraseMap[key]);
    }
    return output;
  };

  const translateDuration = (duration: string): string => {
    const value = normalizeText(duration);
    if (!value) return '';
    return translatePhrase(value);
  };

  const getMealPhrase = (
      when: string,
      slot: 'morning' | 'afternoon' | 'night'
  ): string => {
    const normalizedWhen = normalizeText(when).toLowerCase();
    const mealMap: Record<string, Record<'morning' | 'afternoon' | 'night', string>> = {
      'after food':    { morning: 'after breakfast',  afternoon: 'after lunch',  night: 'after dinner'  },
      'before food':   { morning: 'before breakfast', afternoon: 'before lunch', night: 'before dinner' },
      'with food':     { morning: 'with breakfast',   afternoon: 'with lunch',   night: 'with dinner'   },
      'empty stomach': { morning: 'empty stomach',    afternoon: 'empty stomach',night: 'empty stomach' },
      'at bedtime':    { morning: 'at bedtime',       afternoon: 'at bedtime',   night: 'at bedtime'    },
      'bedtime':       { morning: 'at bedtime',       afternoon: 'at bedtime',   night: 'at bedtime'    },
    };
    return mealMap[normalizedWhen]?.[slot] ?? slot;
  };

  // ── Dosage parser — handles 3-part, 4-part, ½, ml suffixes ──────────────────

  const calculateTimingString = (med: any): string => {
    const dosage   = normalizeText(med?.dosage);
    const when     = normalizeText(med?.taken_when ?? med?.when);
    const duration = normalizeText(med?.duration);

    if (!dosage) return duration ? translateDuration(duration) : '';

    const dosageLower = dosage.toLowerCase();

    if (dosageLower === 'sos')         return t.sos ?? 'SOS';
    if (dosageLower === 'as directed') return t.asDirected ?? 'As Directed';
    if (dosageLower === 'hs' || dosageLower === 'bedtime') {
      const base = translatePhrase('at bedtime');
      return duration ? `${base} - ${translateDuration(duration)}` : base;
    }
    if (dosageLower === 'od') {
      const base = `1 ${getMealPhrase(when || 'after food', 'morning')}`;
      return duration ? `${translatePhrase(base)} - ${translateDuration(duration)}` : translatePhrase(base);
    }
    if (dosageLower === 'bd') {
      const base = [
        `1 ${getMealPhrase(when || 'after food', 'morning')}`,
        `1 ${getMealPhrase(when || 'after food', 'night')}`,
      ].join(', ');
      return duration ? `${translatePhrase(base)} - ${translateDuration(duration)}` : translatePhrase(base);
    }
    if (dosageLower === 'tds' || dosageLower === 'tid') {
      const base = [
        `1 ${getMealPhrase(when || 'after food', 'morning')}`,
        `1 ${getMealPhrase(when || 'after food', 'afternoon')}`,
        `1 ${getMealPhrase(when || 'after food', 'night')}`,
      ].join(', ');
      return duration ? `${translatePhrase(base)} - ${translateDuration(duration)}` : translatePhrase(base);
    }

    const parts = dosage.split('-').map((p: string) => normalizeText(p));

    if (parts.length === 3) {
      // Standard M-A-N
      const slots: Array<'morning' | 'afternoon' | 'night'> = ['morning', 'afternoon', 'night'];
      const instructions: string[] = [];
      parts.forEach((part, i) => {
        if (!isZeroLike(part)) {
          instructions.push(`${part} ${getMealPhrase(when, slots[i])}`);
        }
      });
      let result = translatePhrase(instructions.join(', '));
      if (duration) result = result ? `${result} - ${translateDuration(duration)}` : translateDuration(duration);
      return result;
    }

    if (parts.length === 4) {
      // 4-part M-A-E-N (four times daily)
      const slots4 = ['morning', 'afternoon', 'evening', 'night'] as const;
      const mealSlots: Array<'morning' | 'afternoon' | 'night'> = ['morning', 'afternoon', 'night', 'night'];
      const instructions: string[] = [];
      parts.forEach((part, i) => {
        if (!isZeroLike(part)) {
          instructions.push(`${part} ${slots4[i]}`);
        }
      });
      let result = translatePhrase(instructions.join(', '));
      if (when) result = result ? `${result} ${translatePhrase(when)}` : '';
      if (duration) result = result ? `${result} - ${translateDuration(duration)}` : translateDuration(duration);
      return result;
    }

    // Single value or unknown format — use as-is with when
    const fallback = when ? `${dosage} ${when}` : dosage;
    return duration
        ? `${translatePhrase(fallback)} - ${translateDuration(duration)}`
        : translatePhrase(fallback);
  };

  const buildDirectionsCell = (med: any): string => {
    const when     = normalizeText(med?.taken_when ?? med?.when);
    const duration = normalizeText(med?.duration);
    const translatedWhen     = when     ? translatePhrase(when)     : '';
    const translatedDuration = duration ? translateDuration(duration) : '';
    if (translatedWhen && translatedDuration) return `${translatedWhen} - ${translatedDuration}`;
    return translatedWhen || translatedDuration || '';
  };

  const medicineNameOf = (med: any): string =>
      normalizeText(med?.product || med?.brand_name || med?.generic_name);

  // ── Derived info ──────────────────────────────────────────────────────────────

  const clinicName    = clinicInfo?.name    || '';
  const clinicAddress = clinicInfo?.address || '';
  const clinicPhone   = clinicInfo?.phone   || '';
  const doctorName    = settings?.profile?.name   || doctorInfo?.name    || '';
  const doctorSpec    = settings?.profile?.specialty || doctorInfo?.specialization || doctorInfo?.specialty || '';
  const doctorLicense = settings?.profile?.license   || doctorInfo?.registration_no || '';

  // ── Render — original layout preserved, only additions noted ─────────────────

  return (
      <>
        {/* ── Clinic header (configurable) ─────────────────────────────────── */}
        {(!headerConfig || headerConfig.mode === 'auto') && clinicName && (
            <div style={{ textAlign: 'center', marginBottom: '6mm', borderBottom: '2px solid #000', paddingBottom: '4mm' }}>
              {(!headerConfig || headerConfig.showName) && (
                  <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '1mm' }}>{clinicName}</div>
              )}
              {(!headerConfig || headerConfig.showAddress) && clinicAddress && (
                  <div style={{ fontSize: '10px', color: '#444', marginBottom: '1mm' }}>{clinicAddress}</div>
              )}
              {(!headerConfig || headerConfig.showPhone) && clinicPhone && (
                  <div style={{ fontSize: '10px', color: '#444' }}>Ph: {clinicPhone}</div>
              )}
            </div>
        )}
        {headerConfig?.mode === 'none' && (
            <div style={{ height: `${headerConfig.topSpaceMm}mm` }} />
        )}
        {headerConfig?.mode === 'custom' && headerConfig.customText && (
            <div style={{ textAlign: 'center', marginBottom: '6mm', borderBottom: '2px solid #000', paddingBottom: '4mm', whiteSpace: 'pre-wrap' }}>
              {headerConfig.customText}
            </div>
        )}

        {/* ── ORIGINAL: Patient + Doctor grid ──────────────────────────────── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '10mm',
          marginBottom: '8mm',
          fontSize: '11px',
          borderBottom: '1px solid #000',
        }}>
          <div>
            <div style={{ marginBottom: '2mm' }}>
              <span style={{ fontWeight: 'bold' }}>Patient Name:</span>{' '}
              {PrintUtil.toUpperCaseText(patientInfo?.name) || PrintUtil.toUpperCaseText(patientInfo?.patient_name)}
            </div>
            <div style={{ marginBottom: '2mm' }}>
              <span style={{ fontWeight: 'bold' }}>Age / Gender:</span>{' '}
              {PrintUtil.generateAgeGenderString(patientInfo)}
            </div>
            <div style={{ marginBottom: '2mm' }}>
              <span style={{ fontWeight: 'bold' }}>Phone:</span>{' '}
              {patientInfo?.phone || data?.patient_phone}
            </div>
            <div style={{ marginBottom: '2mm' }}>
              <span style={{ fontWeight: 'bold' }}>Date:</span>{' '}
              {DateUtil.formatDateForType(data?.date, 'date-only')}
            </div>
          </div>
          <div>
            <div style={{ marginBottom: '2mm' }}>
              <span style={{ fontWeight: 'bold' }}>Doctor Name:</span>{' '}
              Dr. {doctorName || data?.doctor_name}
            </div>
            <div style={{ marginBottom: '2mm' }}>
              <span style={{ fontWeight: 'bold' }}>MRN:</span>{' '}
              {patientInfo?.mrn || data?.patient_mrn || 'N/A'}
            </div>
            <div style={{ marginBottom: '2mm' }}>
              <span style={{ fontWeight: 'bold' }}>Department:</span>{' '}
              {doctorSpec}
            </div>
            {doctorLicense && (
                <div style={{ marginBottom: '2mm' }}>
                  <span style={{ fontWeight: 'bold' }}>Reg No:</span>{' '}
                  {doctorLicense}
                </div>
            )}
          </div>
        </div>

        {/* ── ORIGINAL: Complaints ─────────────────────────────────────────── */}
        {data?.complaints?.some((c: any) => c.complaint?.trim()) && (
            <div style={{ marginBottom: '4mm', fontSize: '11px' }}>
              <span style={{ fontWeight: 'bold', marginRight: '5px' }}>Complaints:</span>
              <span>
            {data.complaints
                .filter((c: any) => c.complaint?.trim())
                .map((c: any) =>
                    `${c.complaint.toUpperCase()}${c.duration
                        ? c.duration.toLowerCase().startsWith('since')
                            ? ` ${c.duration}`
                            : ` since ${c.duration}`
                        : ''
                    }`
                )
                .join(' , ')}
          </span>
            </div>
        )}

        {/* ── ADDED: Diagnosis ─────────────────────────────────────────────── */}
        {hasText(data?.diagnosis) && (
            <div style={{ marginBottom: '4mm', fontSize: '11px' }}>
              <span style={{ fontWeight: 'bold', marginRight: '5px' }}>Diagnosis:</span>
              <span style={{ fontWeight: '500' }}>{data.diagnosis}</span>
            </div>
        )}

        {/* ── ORIGINAL: Vitals + ADDED Weight/Temp ─────────────────────────── */}
        {data?.vitals && (
            data.vitals.bp || data.vitals.pulse || data.vitals.spo2 ||
            data.vitals.weight || data.vitals.temp
        ) && (
            <div style={{ marginBottom: '6mm', fontSize: '11px', fontWeight: 'bold' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                {data.vitals.bp     && <div>BP {data.vitals.bp} mmHg |</div>}
                {data.vitals.pulse  && <div>Pulse {data.vitals.pulse} bpm |</div>}
                {data.vitals.spo2   && <div>SPO2 {data.vitals.spo2}% |</div>}
                {data.vitals.weight && <div>Wt {data.vitals.weight} kg |</div>}
                {data.vitals.temp   && <div>Temp {data.vitals.temp}°F</div>}
              </div>
            </div>
        )}

        {/* ── ORIGINAL: Medicines — FIXED timing logic ──────────────────────── */}
        {data?.medicines?.some((m: any) => medicineNameOf(m)) && (
            <div style={{ marginBottom: '8mm' }}>
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '4mm' }}>℞</div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '50% 12% 30% 8%',
                borderBottom: '1px solid #000',
                paddingBottom: '2mm', marginBottom: '3mm',
                fontSize: '10px', fontWeight: 'bold',
              }}>
                <div style={{ textAlign: 'left' }}>Medicine</div>
                <div style={{ textAlign: 'left' }}>Dosage</div>
                <div style={{ textAlign: 'left' }}>Directions</div>
                <div style={{ textAlign: 'center' }}>Qty</div>
              </div>

              {data.medicines
                  .filter((m: any) => medicineNameOf(m))
                  .map((med: any, index: number) => {
                    // FIXED: use only buildDirectionsCell (when + duration)
                    // calculateTimingString available for languages that need spelled-out timing
                    const directions   = language === 'en'
                        ? buildDirectionsCell(med)
                        : calculateTimingString(med);
                    const medicineName = medicineNameOf(med);

                    return (
                        <div
                            key={index}
                            style={{
                              marginBottom: '2mm', marginTop: '1mm',
                              fontSize: '11px', borderBottom: '1px solid #ddd',
                            }}
                        >
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '50% 15% 30% 5%',
                            alignItems: 'start', marginBottom: '1mm',
                          }}>
                            <div style={{ fontWeight: 'bold' }}>
                              <span style={{ fontWeight: 'bold' }}>{index + 1})</span>{' '}
                              {medicineName.toUpperCase()}
                              {hasText(med.generic_name) && (
                                  <div style={{ fontWeight: 'normal', fontSize: '9px', color: '#555', marginTop: '0.5mm' }}>
                                    {normalizeText(med.generic_name).toUpperCase()}
                                  </div>
                              )}
                            </div>
                            <div style={{ textAlign: 'left', fontWeight: 'bold' }}>
                              {normalizeText(med.dosage)}
                            </div>
                            <div style={{ textAlign: 'left', fontWeight: '500' }}>
                              {directions}
                            </div>
                            <div style={{ textAlign: 'center', fontWeight: 'bold' }}>
                              {med.quantity != null && med.quantity !== '' ? String(med.quantity) : ''}
                            </div>
                          </div>

                          {hasText(med.notes) && (
                              <div style={{ marginLeft: '15px', fontSize: '10px', color: '#333', marginBottom: '1mm' }}>
                                <span style={{ fontWeight: 'bold', marginRight: '5px' }}>Notes:</span>
                                <span style={{ color: '#0066cc', fontWeight: '500' }}>
                        {normalizeText(med.notes)}
                      </span>
                              </div>
                          )}
                        </div>
                    );
                  })}
            </div>
        )}

        {/* ── ORIGINAL: Investigations ──────────────────────────────────────── */}
        {data?.investigations?.some((i: any) =>
            (typeof i === 'string' && i.trim()) ||
            (i && typeof i.investigation === 'string' && i.investigation.trim())
        ) && (
            <div style={{ marginBottom: '4mm', fontSize: '11px' }}>
              <span style={{ fontWeight: 'bold', marginRight: '5px' }}>{t.investigations}</span>
              <span>
            {data.investigations
                .map((i: any) => typeof i === 'string' ? i : (i?.investigation || ''))
                .filter(Boolean)
                .join(', ')}
          </span>
            </div>
        )}

        {/* ── ORIGINAL: Next Visit ──────────────────────────────────────────── */}
        {data?.next_visit?.date && (
            <div style={{ marginBottom: '8mm', fontSize: '11px' }}>
              <span style={{ fontWeight: 'bold', marginRight: '5px' }}>{t.nextVisit}</span>
              <span style={{ color: '#0066cc', fontWeight: '500' }}>
            {DateUtil.formatDateForType(data.next_visit.date, 'date-only')}
                {data.next_visit.time ? ` at ${DateUtil.formatDateForType(data.next_visit.time, 'time-only')}` : ''}
          </span>
            </div>
        )}

        {/* ── ORIGINAL: Advice ──────────────────────────────────────────────── */}
        {hasText(data?.advice) && (
            <div style={{ marginBottom: '8mm', fontSize: '11px' }}>
              <span style={{ fontWeight: 'bold', marginRight: '5px' }}>{t.advice}</span>
              <div style={{ color: '#0066cc', fontWeight: '500', marginTop: '2mm', lineHeight: '1.4' }}>
                {data.advice}
              </div>
            </div>
        )}

        {/* ── Custom Fields ──────────────────────────────────────────────────── */}
        {settings?.prescriptionLayout?.customFields
            ?.filter((cf: any) => cf.enabled && cf.label?.trim() && hasText(data?.custom_fields?.[cf.key]))
            .map((cf: any) => (
                <div key={cf.key} style={{ marginBottom: '8mm', fontSize: '11px' }}>
                  <span style={{ fontWeight: 'bold', marginRight: '5px' }}>{cf.label}:</span>
                  <div style={{ color: '#0066cc', fontWeight: '500', marginTop: '2mm', lineHeight: '1.4' }}>
                    {data.custom_fields[cf.key]}
                  </div>
                </div>
            ))}

        {/* ── ORIGINAL: Signature ───────────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '15mm', paddingTop: '10mm' }}>
          <div style={{ textAlign: 'left', fontSize: '10px', fontWeight: 'bold', lineHeight: '1.2' }}>
            {settings?.profile?.signature
                ? settings.profile.signature.split('\n').map((line: string, i: number) => (
                    <span key={i}>{line}<br /></span>
                ))
                : <>
                  {doctorName && <div>Dr. {doctorName}</div>}
                  {clinicName && <div>{clinicName}</div>}
                  {doctorSpec && <div>{doctorSpec}</div>}
                </>
            }
          </div>
        </div>

        {/* ── ORIGINAL: Footer ──────────────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10mm', fontSize: '10px' }}>
          <div>
            <span style={{ fontWeight: 'bold', marginBottom: '5mm' }}>Generated By </span>
            <span style={{ padding: '3mm' }}>{currentUser?.name}</span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontWeight: 'bold', marginBottom: '3mm' }}>Generated On </span>
            <span style={{ padding: '3mm' }}>
            {DateUtil.formatDateForType(new Date().toISOString(), 'date-time')}
          </span>
          </div>
        </div>
      </>
  );
}